import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Tooltip } from './Shared';

const SEGMENTS = [
  'GCC',
  'CX and Contact Center',
  'Healthcare',
  'KPO',
  'IT & Software Development',
  'Animation and Creative Services',
  'Game Development',
];

const SUPPORT_SERVICES = [
  'Real Estate and Commercial Spaces',
  'Telecommunications & Infrastructure',
  'Human Resources',
  'Energy',
  'Financial Services',
];

function SegmentButton({ segment, label, isSelected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(segment)}
      className={`p-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-between gap-2 text-left ${
        isSelected
          ? 'border-accent bg-accent/5 text-accent'
          : 'border-slate-200 text-slate-600 hover:border-slate-300'
      }`}
      data-testid={`segment-${segment}`}
    >
      <span>{label || segment}</span>
      {isSelected && <CheckCircle className="w-4 h-4 flex-shrink-0" />}
    </button>
  );
}

export function SectionA({ data, updateSection, toggleBusinessSegment }) {
  return (
    <div className="space-y-6" data-testid="section-a-content">
      <div>
        <Tooltip text="Enter your total number of employees (full-time equivalent) at year-end. Include permanent and fixed-term employees. Exclude contractors unless directly managed on payroll. Data source: HR/Payroll system year-end report. If need more information, click the AI Assist button.">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            A1. Total Employees (Year-End)
          </label>
        </Tooltip>
        <input
          type="number"
          value={data.section_a.total_employees || ''}
          onChange={(e) => updateSection('section_a', 'total_employees', parseInt(e.target.value) || 0)}
          className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20"
          placeholder="e.g., 1250"
          data-testid="total-employees-input"
        />
      </div>

      <div>
        <Tooltip text="Select all business segments that apply to your organization. You may select multiple options. Data source: Company registration, business unit breakdown. If need more information, click the AI Assist button.">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            A2. Business Segments (Select all that apply)
          </label>
        </Tooltip>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {SEGMENTS.map((segment) => (
            <SegmentButton
              key={segment}
              segment={segment}
              isSelected={(data.section_a.business_segments || []).includes(segment)}
              onToggle={toggleBusinessSegment}
            />
          ))}
        </div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-4 mb-2">Support Services</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {SUPPORT_SERVICES.map((sub) => {
            const value = `Support Services – ${sub}`;
            return (
              <SegmentButton
                key={value}
                segment={value}
                label={sub}
                isSelected={(data.section_a.business_segments || []).includes(value)}
                onToggle={toggleBusinessSegment}
              />
            );
          })}
        </div>
        {(data.section_a.business_segments || []).length > 0 && (
          <p className="text-xs text-slate-500 mt-2">
            Selected: {(data.section_a.business_segments || []).join(', ')}
          </p>
        )}
      </div>

      <div>
        <Tooltip text="Include delivery centers and major operational facilities. Data source: Facilities/Admin department, lease agreements. If need more information, click the AI Assist button.">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            A3. Total Operational Sites in PH
          </label>
        </Tooltip>
        <input
          type="number"
          value={data.section_a.operational_sites || ''}
          onChange={(e) => updateSection('section_a', 'operational_sites', parseInt(e.target.value) || 0)}
          className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20"
          placeholder="e.g., 3"
          data-testid="operational-sites-input"
        />
      </div>
    </div>
  );
}
